import {flow, isEmpty, join, keys, reduce} from 'lodash/fp';

import {CLIENT_REPORT, COACH_REPORT} from '/enum/popover.enum';

import FirebaseAnalyticsBackend from './analytics-firebase';

// Set this to `true` to enable console logging of analytic events.
// Do NOT commit that change into the repo.
const LOG_ANALYTICS = false;

const uncappedReduce = reduce.convert({cap: false});

export class AnalyticsUtil {
  backend: any;

  screenName: any;

  screenParams: any;

  constructor({backend}: any) {
    this.backend = backend;

    this.screenName = null;
    this.screenParams = null;
  }

  disableAnalyticsCollection() {
    this.backend.setAnalyticsCollectionStatus(false);
  }

  enableAnalyticsCollection() {
    this.backend.setAnalyticsCollectionStatus(true);
  }

  trackUserId({optaviaId}: any) {
    this.backend.logUserId(optaviaId);
  }

  trackUserProperties({
    coachId,
    competencyView,
    currentRank,
    highestAchievedRank,
    certificationStatus,
  }: any) {
    this.backend.logUserProperties({
      coach_id: coachId,
      competency_view: competencyView,
      current_rank: currentRank,
      highest_achieved_rank: highestAchievedRank,
      certification_status: certificationStatus,
    });
  }

  trackScreenName({screenName, screenParams}: any) {
    this.screenName = screenName;
    this.screenParams = screenParams;
    this.backend.logCurrentScreen(this.getReportingScreenName());
  }

  trackReportCardView({recordsName, recordsCardName}: any) {
    let reportCardScreenName = 'report-card';
    if (recordsCardName === CLIENT_REPORT) {
      reportCardScreenName = 'client-report-card';
    } else if (recordsCardName === COACH_REPORT) {
      reportCardScreenName = 'coach-report-card';
    }

    this.backend.logEvent('report_card_view_details', {
      screen_name: reportCardScreenName,
      parent_screen: this.getReportingScreenName(),
      report_name: recordsName,
    });
  }

  trackReportFilters({appliedFilters}: any) {
    if (isEmpty(appliedFilters)) {
      this.backend.logEvent('filter_selection', {
        screen_name: this.getReportingScreenName(),
        filter_category: '',
        filter_selected: '',
      });
    } else {
      const filteredAttributes = flow([keys, join(', ')])(appliedFilters);
      const filterDescriptions = flow([
        uncappedReduce(
          (result: any, attributeFilter: any, attributeName: any) => [
            ...result,
            `${attributeName} ${attributeFilter}`,
          ],
          [],
        ),
        join(', '),
      ])(appliedFilters);

      this.backend.logEvent('filter_selection', {
        screen_name: this.getReportingScreenName(),
        filter_category: filteredAttributes,
        filter_selected: filterDescriptions,
      });
    }
  }

  trackNavigationalClick({menuName, targetName}: any) {
    this.backend.logEvent('navigation_click', {
      screen_name: this.getReportingScreenName(),
      menu_type: menuName,
      menu_action: targetName,
    });
  }

  trackReportMetricClick({recordsName}: any) {
    this.backend.logEvent('cta_link_click', {
      screen_name: this.getReportingScreenName(),
      link_name: recordsName,
    });
  }

  trackExpandPerformanceTarget({targetName}: any) {
    this.backend.logEvent('expand_section', {
      screen_name: this.getReportingScreenName(),
      expanded_area: targetName,
    });
  }

  trackNonFunctionalClick({elementName}: any) {
    let eventScreenName = this.getReportingScreenName();

    this.backend.logEvent('non_functional_click', {
      screen_name: eventScreenName,
      element_name: elementName,
    });
  }

  getReportingScreenName() {
    if (this.screenName === 'card') {
      const {modalName} = this.screenParams || {};
      if (modalName === 'USER_PROFILE') {
        return 'user-profile';
      }
    }

    if (this.screenName === 'records-main-screen') {
      return 'report';
    }

    if (this.screenName === 'records-record-screen') {
      const {recordType} = this.screenParams || {};
      if (recordType === 'COACH_PROFILE') {
        return 'coach-profile';
      } else if (recordType === 'CLIENT_PROFILE') {
        return 'client-profile';
      }
    }

    return this.screenName;
  }
}

let AnalyticsUtilDefaultInstance: any = null;

export const getAnalyticsUtilDefaultInstance = () => {
  if (AnalyticsUtilDefaultInstance === null) {
    AnalyticsUtilDefaultInstance = new AnalyticsUtil({
      backend: new FirebaseAnalyticsBackend({verbose: LOG_ANALYTICS}),
    });
  }
  return AnalyticsUtilDefaultInstance;
};
